import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const FilterDrawer = ({
  filters,
  onFilterChange,
  categories,
  onClearAll,
  activeFilterCount,
  open,
  onOpenChange
}) => {

  // Available sizes in standard order
  const availableSizes = ['Twin', 'Twin XL', 'Full', 'Queen', 'King', 'California King'];

  // Sort options with labels
  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'name_asc', label: 'Name: A-Z' },
    { value: 'name_desc', label: 'Name: Z-A' }
  ];

  // Handle size checkbox changes
  const handleSizeChange = (size, checked) => {
    const currentSizes = filters.sizes || [];
    let newSizes;

    if (checked) {
      newSizes = [...currentSizes, size];
    } else {
      newSizes = currentSizes.filter(s => s !== size);
    }

    onFilterChange('sizes', newSizes);
  };

  // Handle category checkbox changes
  const handleCategoryChange = (categoryValue, checked) => {
    if (checked) {
      onFilterChange('category', categoryValue);
    } else {
      onFilterChange('category', '');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <Button variant="outline" className="lg:hidden relative flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <Badge variant="destructive" className="ml-1">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-[80vw] sm:max-w-[360px] overflow-y-auto p-4">
        <SheetHeader className="pb-3">
          <SheetTitle className="text-lg">Filters & Sort</SheetTitle>
        </SheetHeader>

        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            className="w-full mt-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 h-9"
            onClick={() => {
              onClearAll();
              onOpenChange(false);
            }}
          >
            Clear All Filters
          </Button>
        )}

        <Accordion type="multiple" defaultValue={["category", "sort"]} className="mt-6 space-y-4">
          {/* Category Section */}
          <AccordionItem value="category" className="border-none bg-gray-50 rounded-xl px-4">
            <AccordionTrigger className="text-base font-bold text-gray-900 hover:no-underline py-4">
              <span className="flex items-center gap-3">
                <span>Category</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <div className="space-y-4">
                {categories.map((cat) => {
                  const isSelected = filters.category === cat.value;
                  return (
                    <div
                      key={cat.value}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${isSelected
                        ? 'bg-blue-50 border-brand-blue/30 text-brand-blue'
                        : 'bg-white border-transparent text-gray-700'
                        }`}
                      onClick={() => handleCategoryChange(cat.value, !isSelected)}
                    >
                      <Label
                        htmlFor={`mobile-category-${cat.value}`}
                        className="text-sm font-medium cursor-pointer flex-1"
                      >
                        {cat.label}
                      </Label>
                      <Checkbox
                        id={`mobile-category-${cat.value}`}
                        checked={isSelected}
                        onCheckedChange={(checked) => handleCategoryChange(cat.value, checked)}
                        className="h-5 w-5 border-gray-300 data-[state=checked]:bg-brand-blue data-[state=checked]:border-brand-blue"
                      />
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Sort Section */}
          <AccordionItem value="sort" className="border-none bg-gray-50 rounded-xl px-4">
            <AccordionTrigger className="text-base font-bold text-gray-900 hover:no-underline py-4">
              <span className="flex items-center gap-3">
                <span>Sort By</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <RadioGroup
                value={filters.sort || 'newest'}
                onValueChange={(value) => onFilterChange('sort', value)}
                className="space-y-3"
              >
                {sortOptions.map((option) => {
                  const isSelected = (filters.sort || 'newest') === option.value;
                  return (
                    <div
                      key={option.value}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${isSelected
                        ? 'bg-blue-50 border-brand-blue/30 text-brand-blue'
                        : 'bg-white border-transparent text-gray-700'
                        }`}
                    >
                      <Label
                        htmlFor={`mobile-sort-${option.value}`}
                        className="text-sm font-medium cursor-pointer flex-1"
                      >
                        {option.label}
                      </Label>
                      <RadioGroupItem
                        value={option.value}
                        id={`mobile-sort-${option.value}`}
                        className="h-5 w-5 border-gray-300 text-brand-blue focus:ring-brand-blue"
                      />
                    </div>
                  );
                })}
              </RadioGroup>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="mt-8 space-y-3">
          <Button
            className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white h-12 rounded-xl font-bold shadow-lg shadow-blue-200"
            onClick={() => onOpenChange(false)}
          >
            Apply Filters
          </Button>

          {activeFilterCount > 0 && (
            <Button
              variant="outline"
              className="w-full h-12 rounded-xl border-gray-200 text-gray-500 hover:text-red-600 hover:bg-red-50 hover:border-red-100"
              onClick={() => {
                onClearAll();
                onOpenChange(false);
              }}
            >
              Clear All
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FilterDrawer;

